import { isObject, isUndefined, scopedErrorMessage, subscribeToHmrModuleChanges } from '@philips-internal/oswald-ui-utils';
import { Address, EventMapper } from '../component/event/index.js';
import MetaValidator, { validateLocales } from '../utils/meta-validator.js';
import { findStackTraceEntryOfDecorator } from '../utils/stack-trace.js';
import { get, isController } from './controller.js';
const scope = 'decorators:event';
/**
 * Validates if a given value is a valid target.
 *
 * @param value - the value to be validated
 */
const validateTarget = (value) => {
    return typeof value === 'string' || Array.isArray(value);
};
const paramsValidator = MetaValidator.create()
    .str('type', true)
    .custom('target', false, validateTarget)
    .custom('locales', false, validateLocales)
    .validator();
/**
 * Event decorator for event listeners used within Controllers.
 *
 * @param definition - class where the decorator is being used.
 * @param name - name of the decorated method.
 * @param descriptor - property descriptor of the method.
 * @param params - the decorator parameters.
 */
function InternalEventListener(definition, name, descriptor, params) {
    const module = findStackTraceEntryOfDecorator();
    setTimeout(() => {
        const definitionName = definition.constructor.name;
        const { locales: eventLocales, target: eventTarget, type } = params;
        if (!isController(definition)) {
            scopedErrorMessage(scope, `The class ${definitionName} is not a valid controller. Either decorate it with @Controller or remove the @${type} event decorator from ${name}`);
        }
        const { component: definitionTarget, locales: definitionLocales, fragment } = get(definition);
        const locales = eventLocales ?? definitionLocales;
        const target = eventTarget ?? definitionTarget;
        for (const locale of locales) {
            if (Array.isArray(definitionLocales) && !definitionLocales.includes(locale)) {
                throw new Error(`[${scope}]: Locale ${locale} of event decorator @${type} in ${definitionName}.${name} is not supported by its controller`);
            }
        }
        const addressLocales = locales.length > 0 ? locales : definitionLocales;
        const addresses = createAddress(definitionName, name, addressLocales);
        const targets = Array.isArray(target) ? target : [target];
        // Iterate over all addresses and targets, checking and throwing if there's already a register of the given event
        for (const address of addresses) {
            for (const target of targets) {
                const component = EventMapper.getComponent(fragment, target);
                if (!component) {
                    continue;
                }
                if (component.has(type, address)) {
                    const baseMessage = `Fragment ${fragment} already has event decorator @${type} registered for target ${target}`;
                    const localeMessage = address.locale ? `in locale ${address.locale}` : '';
                    throw new Error(`[${scope}]: ${baseMessage} ${localeMessage}`);
                }
                component.insert(type, address);
                subscribeToHmrModuleChanges(module, () => {
                    component.remove(type);
                });
            }
        }
    });
    return descriptor;
}
/**
 * Function to create all needed addresses taking locales in consideration.
 *
 * @param definitionName - Name of the class where the decorator is being used.
 * @param methodName - Method where the decorator is being applied.
 * @param locales - List of locales where the decorator should be used.
 */
function createAddress(definitionName, methodName, locales = []) {
    const addresses = [];
    for (const locale of locales) {
        addresses.push(new Address(definitionName, methodName, locale));
    }
    if (addresses.length <= 0) {
        addresses.push(new Address(definitionName, methodName));
    }
    return addresses;
}
/**
 * Function to validate if a given decorator meets all of its constraints, returning the decorator function when valid.
 *
 * @example
 * validateEventListener({ target: 'custom-alias', type: 'onClick' })
 *  onClick(): void {
 * }
 *
 * @param definition - the decorator parameters
 */
function validateEventListener(definition) {
    const validate = MetaValidator.validateDecorator(InternalEventListener, paramsValidator, definition);
    if (validate) {
        return validate;
    }
    const { target = '?', type = '?' } = definition;
    throw Error(`[${scope}]: Error validating event decorator @${type} for target ${target}`);
}
/**
 * Generate event decorators to be used on @Controller decorated classes.
 *
 * @example
 * const OnClick = generator('onClick');
 *
 * @param type - the event type.
 */
function generator(type) {
    // `any` is used in the return type above because we can't know where the decorator is being applied,
    // so the return type should be open to all possibilities.
    return function (args) {
        const parameters = { locales: [], target: [], type };
        if (typeof args === 'string') {
            parameters.target = [args];
        }
        if (Array.isArray(args)) {
            parameters.target = [...args];
        }
        if (isObject(args)) {
            if ('target' in args) {
                const target = args.target;
                if (typeof target === 'string') {
                    parameters.target = [target];
                }
                else if (Array.isArray(target)) {
                    parameters.target = [...target];
                }
            }
            // Using `isUndefined` and then negating it, intead of `isDefined`, because of former `value is undefined` type guard;
            if ('locales' in args && !isUndefined(args.locales)) {
                parameters.locales = [...args.locales];
            }
        }
        if (Array.isArray(parameters.target) && parameters.target.length <= 0) {
            throw Error(`[${scope}]: Missing target for ${type} event`);
        }
        return validateEventListener(parameters);
    };
}
export { validateEventListener as default, generator, };
//# sourceMappingURL=event-listener.js.map